// What a campaign detail screen is allowed to claim.
//
// Kept apart from the UI because every decision here is a claim about a live
// outbound dialler — whether a control will work, whether a person was reached,
// whether a number is a measurement or an absence. None of it needs a browser
// to be checked. See campaignDetailRules.test.ts.
//
// Type-only imports are erased at compile time, so this module pulls in no
// service and no axios.
import type { CampaignEngineInfo, VoiceCampaign } from 'api/services/voiceCampaigns.service';

// ── identity ────────────────────────────────────────────────────────────────

/**
 * Campaign ids are OPAQUE STRINGS on this surface and must never be coerced.
 *
 * cpass ids are decimal ("1"); voicex ids are hex. `Number("21833e7a…")` is NaN,
 * and a NaN id reaches the API as "campaign not found" — a real campaign
 * reported as missing. Comparing as strings also keeps "1" and "01" distinct,
 * which numeric comparison would silently merge.
 */
export const sameId = (a?: string | null, b?: string | null): boolean =>
  a != null && b != null && String(a) === String(b);

/** The campaign with this id, or undefined. String comparison, never numeric. */
export const findCampaign = (rows: VoiceCampaign[], id?: string | null): VoiceCampaign | undefined =>
  id == null ? undefined : rows.find(c => sameId(c.id, id));

// ── lifecycle ───────────────────────────────────────────────────────────────

export type LifecycleAction = 'start' | 'pause' | 'resume' | 'stop';

/** Whether a control will do anything, and — when it will not — why. */
export interface ActionState {
  enabled: boolean;
  /** Shown to the reader. Present whenever `enabled` is false. */
  reason?: string;
}

export type LifecycleStates = Record<LifecycleAction, ActionState>;

const KNOWN_STATUSES = ['DRAFT', 'ACTIVE', 'PAUSED', 'COMPLETED', 'ARCHIVED'];

/** The engine's own word for the state, upper-cased. '' when it reported none. */
export const normaliseStatus = (s?: string | null): string => (s || '').trim().toUpperCase();

/**
 * Pause holds back what has not been dialled. It does not reach into a call
 * that is already ringing, on either engine, because a call handed to the
 * switch cannot be recalled. A screen that implies otherwise is promising
 * something no amount of backend work can deliver.
 */
export const PAUSE_CAVEAT =
  'Holds back everything not yet dialled. A call already ringing is not recalled — once handed to the '
  + 'switch it cannot be.';

/** Stop is COMPLETED, not a delete: the list of who was never reached survives. */
export const STOP_CAVEAT =
  'Ends the campaign. Everything still waiting is abandoned rather than deleted, so you keep the record '
  + 'of who was never reached. It cannot be started again.';

const ENDED = 'This campaign has ended.';

/**
 * Which lifecycle controls this status permits, with the reason for each refusal.
 *
 * Mirrors the transitions both engines enforce: start from DRAFT, pause from
 * ACTIVE, resume from PAUSED, stop from anything still open. An unrecognised or
 * missing status disables everything rather than guessing — offering Start on a
 * state we cannot read is exactly the lie this function exists to prevent.
 */
export const lifecycleStates = (rawStatus?: string | null): LifecycleStates => {
  const s = normaliseStatus(rawStatus);

  if (!KNOWN_STATUSES.includes(s)) {
    const why = s
      ? `The engine reports this campaign as "${s}", which this screen does not know how to act on.`
      : 'The engine did not report a status for this campaign.';
    return { start: { enabled: false, reason: why }, pause: { enabled: false, reason: why },
             resume: { enabled: false, reason: why }, stop: { enabled: false, reason: why } };
  }

  const on = (yes: boolean, reason: string): ActionState => (yes ? { enabled: true } : { enabled: false, reason });

  return {
    start: on(s === 'DRAFT',
      s === 'ACTIVE' ? 'Already running.'
        : s === 'PAUSED' ? 'Paused, not unstarted — use Resume to carry on.'
          : s === 'ARCHIVED' ? 'Archived campaigns cannot be started.'
            : `${ENDED} A stopped campaign cannot be started again.`),
    pause: on(s === 'ACTIVE',
      s === 'DRAFT' ? 'Nothing is dialling yet.'
        : s === 'PAUSED' ? 'Already paused.'
          : ENDED),
    resume: on(s === 'PAUSED',
      s === 'DRAFT' ? 'Never started — use Start.'
        : s === 'ACTIVE' ? 'Already running.'
          : ENDED),
    stop: on(s === 'DRAFT' || s === 'ACTIVE' || s === 'PAUSED',
      s === 'COMPLETED' ? 'Already stopped.' : 'Archived campaigns are already closed.'),
  };
};

// ── counters ────────────────────────────────────────────────────────────────

/**
 * A counter, or null when the engine did not report one.
 *
 * The distinction is the point. Zero completed calls is a measurement worth
 * showing; a missing field means this engine does not count that, and turning
 * it into 0 invents a measurement nobody made.
 */
export const counterOf = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

/** A counter for display: the number, or an em dash when it was never measured. */
export const formatCounter = (v: unknown): string => {
  const n = counterOf(v);
  return n === null ? '—' : n.toLocaleString();
};

/**
 * The success rate as a percentage — or an em dash.
 *
 * NEVER defaulted to 0. The engine returns null when nothing has been dialled,
 * and "0%" in that box is a claim that every call failed.
 */
export const formatSuccessRate = (v: unknown): string => {
  const n = counterOf(v);
  if (n === null) return '—';
  // 87.50 reads better as 87.5%, and 100.00 as 100%.
  return `${Number(n.toFixed(2))}%`;
};

/** Why the rate is blank, when it is. */
export const successRateNote = (rate: unknown, total: unknown): string | null => {
  if (counterOf(rate) !== null) return null;
  return counterOf(total) === 0
    ? 'Not measured — nothing has been dialled yet.'
    : 'Not measured — this engine did not report a rate.';
};

// ── per-call outcomes ───────────────────────────────────────────────────────

/**
 * How an outcome should be read.
 *
 * `unreached` exists so NO_ANSWER and ABANDONED keep their own group. They are
 * NOT failures: nobody picked up, or the campaign was stopped before their turn
 * came. Folding them into "failed" — which is what a three-bucket summary
 * always ends up doing — reports a person who was never rung as a botched call.
 */
export type OutcomeKind = 'waiting' | 'dialling' | 'reached' | 'failed' | 'unreached' | 'unknown';

export interface Outcome {
  /** The engine's own status name. */
  status: string;
  count: number;
  kind: OutcomeKind;
  label: string;
}

const OUTCOMES: { status: string; kind: OutcomeKind; label: string }[] = [
  { status: 'QUEUED', kind: 'waiting', label: 'Queued' },
  { status: 'SCHEDULED', kind: 'waiting', label: 'Scheduled' },
  { status: 'DIALLING', kind: 'dialling', label: 'Dialling' },
  { status: 'COMPLETED', kind: 'reached', label: 'Completed' },
  { status: 'NO_ANSWER', kind: 'unreached', label: 'No answer' },
  { status: 'ABANDONED', kind: 'unreached', label: 'Abandoned' },
  { status: 'FAILED', kind: 'failed', label: 'Failed' },
];

const ORDER = OUTCOMES.map(o => o.status);

/** How one call status should be read, including ones this build does not know. */
export const outcomeKind = (status?: string | null): OutcomeKind =>
  OUTCOMES.find(o => o.status === normaliseStatus(status))?.kind ?? 'unknown';

/** The human name for one call status; unknown ones keep the engine's own word. */
export const outcomeLabel = (status?: string | null): string => {
  const s = normaliseStatus(status);
  return OUTCOMES.find(o => o.status === s)?.label ?? (s || 'Unknown');
};

/**
 * The per-status breakdown, in call-lifecycle order.
 *
 * Statuses the engine did not report are absent rather than shown as 0, because
 * the engine only counts rows that exist. Statuses this build does not model
 * are kept and labelled with the engine's own name — dropping them would hide
 * calls, which is worse than showing a word we cannot translate.
 */
export const outcomeBreakdown = (byStatus?: Record<string, number> | null): Outcome[] => {
  if (!byStatus || typeof byStatus !== 'object') return [];
  return Object.keys(byStatus)
    .map(k => normaliseStatus(k))
    .filter((k, i, all) => k !== '' && all.indexOf(k) === i)
    .sort((a, b) => {
      const ia = ORDER.indexOf(a), ib = ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    })
    .map(status => {
      const raw = Object.keys(byStatus).find(k => normaliseStatus(k) === status)!;
      return { status, count: counterOf(byStatus[raw]) ?? 0, kind: outcomeKind(status), label: outcomeLabel(status) };
    });
};

// ── engine capabilities ─────────────────────────────────────────────────────

/**
 * Whether this engine acts on a field, or merely stores it.
 *
 * Read from the engine endpoint at runtime, never hardcoded: the list is
 * engine-dependent, and a hardcoded copy would go stale the first time an
 * engine learns to honour something.
 *
 * Unknown engine means ENFORCED. Greying out a working control because the
 * engine lookup failed would be its own lie, and the cautious-looking default
 * is the wrong one here.
 */
export const isEnforced = (engine: CampaignEngineInfo | null | undefined, field: string): boolean => {
  const list = engine?.acceptedButNotEnforced;
  if (!Array.isArray(list)) return true;
  return !list.some(f => String(f).trim().toLowerCase() === field.trim().toLowerCase());
};

/** Said next to a control the engine will accept and then ignore. */
export const notEnforcedNote = (engine: CampaignEngineInfo | null | undefined): string => {
  const who = (engine?.dialledBy || engine?.engine || 'this').toString().toLowerCase();
  return `Stored, but the ${who} dialler does not act on it.`;
};

// ── recipients page ─────────────────────────────────────────────────────────

export interface RecipientPage<T = any> {
  rows: T[];
  /** Zero-based, as the engine numbers it. */
  page: number;
  size: number;
  /** Null when the engine did not say — see counterOf. */
  total: number | null;
  totalPages: number | null;
  first: boolean;
  last: boolean;
}

/**
 * A Spring page as the screen needs it.
 *
 * Defensive about the envelope because two engines answer this route: cpass
 * hand-builds the page map, voicex forwards the runtime's. A bare array is
 * accepted too, so a shape change degrades into "one page" rather than an
 * empty table.
 */
export const recipientPage = <T = any>(raw: any, requestedSize: number): RecipientPage<T> => {
  if (Array.isArray(raw)) {
    return { rows: raw, page: 0, size: requestedSize, total: raw.length, totalPages: 1, first: true, last: true };
  }
  const d = raw && typeof raw === 'object' ? raw : {};
  const rows: T[] = Array.isArray(d.content) ? d.content : Array.isArray(d.data) ? d.data : [];
  const page = counterOf(d.number) ?? 0;
  const size = counterOf(d.size) ?? requestedSize;
  const total = counterOf(d.totalElements);
  const totalPages = counterOf(d.totalPages)
    ?? (total !== null && size > 0 ? Math.max(1, Math.ceil(total / size)) : null);
  return {
    rows,
    page,
    size,
    total,
    totalPages,
    first: typeof d.first === 'boolean' ? d.first : page === 0,
    // Falling back to the row count rather than to `true`: a wrong `last` that
    // says "no more" hides recipients, which is the failure that matters.
    last: typeof d.last === 'boolean' ? d.last
      : totalPages !== null ? page >= totalPages - 1
        : rows.length < size,
  };
};

/** "1–50 of 8,214", or what can be said when the engine gave no total. */
export const pageSummary = (p: RecipientPage): string => {
  if (p.rows.length === 0) return p.total === 0 ? 'No recipients' : 'Nothing on this page';
  const from = p.page * p.size + 1;
  const to = from + p.rows.length - 1;
  return p.total === null
    ? `${from.toLocaleString()}–${to.toLocaleString()}`
    : `${from.toLocaleString()}–${to.toLocaleString()} of ${p.total.toLocaleString()}`;
};

// ── attempt history ─────────────────────────────────────────────────────────

/**
 * One dial of one number, as the engine recorded it.
 *
 * `outcome` is the campaign row's status after that attempt — the same
 * vocabulary the Outcome column uses. `dialStatus` is the switch's own word
 * (ANSWER / NOANSWER / BUSY / CONGESTION), kept as supporting detail: it is
 * accurate but it is not the sentence a person came here to read.
 */
export interface CallAttempt {
  attemptNo?: number;
  startedAt?: string | null;
  outcome?: string | null;
  dialStatus?: string | null;
  providerCallId?: string | null;
  lastError?: string | null;
  /** Set when a later hangup report changed what this attempt was first recorded as. */
  correctedAt?: string | null;
}

/** The attempts on a recipient row, oldest first, or [] when none came back. */
export const attemptHistoryOf = (row: any): CallAttempt[] => {
  const raw = row?.attemptHistory;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((a: unknown): a is CallAttempt => !!a && typeof a === 'object')
    .slice()
    .sort((a, b) => (counterOf(a.attemptNo) ?? 0) - (counterOf(b.attemptNo) ?? 0));
};

/**
 * How many attempts the engine has a record OF.
 *
 * Null when the field is absent — an older build of the API, where the question
 * has no answer rather than the answer zero.
 */
export const attemptsRecordedOf = (row: any): number | null => counterOf(row?.attemptsRecorded);

/** The row's own counter: how many times it claims to have been dialled. */
export const attemptsClaimedOf = (row: any): number | null =>
  counterOf(row?.attempts) ?? counterOf(row?.retryCount);

/**
 * Attempts the row counts but has no record of.
 *
 * The history table is newer than the dialler. Rows dialled before it existed
 * carry a counter and nothing else, and those earlier attempts are genuinely
 * gone. Zero when the two agree, and zero when either is unknown — this is only
 * ever used to say something, never to hide the difference.
 */
export const unrecordedAttempts = (row: any): number => {
  const recorded = attemptsRecordedOf(row);
  const claimed = attemptsClaimedOf(row);
  if (recorded === null || claimed === null) return 0;
  return Math.max(0, claimed - recorded);
};

/**
 * Said when the counter and the history disagree.
 *
 * Names the gap rather than quietly showing the smaller number: "dialled 3
 * times, 1 shown" with no explanation reads as a bug in this screen, and
 * silently showing 1 hides two calls that really were placed.
 */
export const unrecordedNote = (row: any): string | null => {
  const missing = unrecordedAttempts(row);
  if (missing === 0) return null;
  const claimed = attemptsClaimedOf(row);
  return `${missing === 1 ? 'An earlier attempt was' : `${missing} earlier attempts were`} not recorded — `
    + `this number was dialled ${claimed} times in total, but only the ones below were kept.`;
};

/** "Dialled twice" — the headline, from whichever count is the larger truth. */
export const attemptsSummary = (row: any): string => {
  const recorded = attemptsRecordedOf(row);
  const claimed = attemptsClaimedOf(row);
  const n = Math.max(recorded ?? 0, claimed ?? 0);
  if (n === 0) return 'Not dialled yet';
  if (n === 1) return 'Dialled once';
  if (n === 2) return 'Dialled twice';
  return `Dialled ${n} times`;
};

/** The engine's raw dial word, or null when it did not report one. */
export const dialDetail = (a: CallAttempt): string | null => {
  const s = String(a?.dialStatus ?? '').trim();
  return s === '' ? null : s.toUpperCase();
};

/** Said under an attempt whose recorded outcome was later revised. */
export const correctionNote = (a: CallAttempt): string | null =>
  a?.correctedAt ? 'Revised after the call ended.' : null;
